import { Footer } from "@components/Footer";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "@helpers/useTranslation";
import { Link } from "react-router-dom";
import { useTranslatedKey } from "@helpers/useTranslatedKey";
import { FirebaseCachedImage } from "@components/FirebaseCachedImage";
import { useEventId } from "src/helpers/useEvent";

type Props = {
  challenge: QuizChallenge;
  solution: QuizSolution;
};

export const Quiz: FC<Props> = ({ challenge, solution }) => {
  const { t } = useTranslation("challenges");
  const [message, setMessage] = useState("");
  const eventId = useEventId();

  const answers = solution?.answers || [];

  const isCorrect = (correctAnswer: number, answerIndex: number) => {
    if (answers.length < answerIndex) return false;
    return correctAnswer === answers[answerIndex];
  };

  const answerExists = (answerIndex: number) => {
    if (answers.length < answerIndex) return false;
    return answers[answerIndex] !== undefined;
  };

  useEffect(() => {
    if (solution) {
      const correctQuestions = challenge.questions.reduce((acc, q, index) => {
        if (isCorrect(q.correctAnswer, index)) {
          return acc + 1;
        }
        return acc;
      }, 0);

      const allQuestions = challenge.questions.length;
      setMessage(`${t("answered")} ${correctQuestions} / ${allQuestions} ${t("correctly")}!`);
    }
  }, [solution]);

  const { getTranslatedValue } = useTranslatedKey();
  const about = getTranslatedValue(challenge, "about");
  const copy = getTranslatedValue(challenge, "copy");

  if (solution?.completed) {
    return (
      <main>
        <div className="flex max-w-lg flex-col justify-center px-4">
          <h1 className="mt-16 px-10 text-center text-3xl font-semibold">{t("quizChallengeCompleted")}</h1>
          <div className="mt-4 text-center text-lg">{t("challengeDoneSummary")}</div>
        </div>
        <p className="mt-16 mb-10 px-10 text-center text-lg font-bold">{message}</p>
        {challenge.questions.map((q, index) => {
          if (!answerExists(index)) return <></>;

          const correct = isCorrect(q.correctAnswer, index);
          const answerKeys = ["answer0", "answer1", "answer2", "answer3"] as const;
          const correctAnswer = getTranslatedValue(q, answerKeys[q.correctAnswer]) ?? "";
          const yourAnswer = getTranslatedValue(q, answerKeys[answers[index]]) ?? "";

          // Construct photo URI if question has a photo
          const photoUri =
            q.questionHasPhoto && q.id ? `events/${eventId}/challenges/${challenge.id}/${q.id}_light` : undefined;

          if (challenge.showCorrectAnswers) {
            return (
              <div key={q.question} className="text-md my-24 px-10 text-center">
                {photoUri && (
                  <div className="mb-6 flex justify-center">
                    <div className="w-full max-w-2xs">
                      <FirebaseCachedImage key={photoUri} uri={photoUri} alt={getTranslatedValue(q, "question")} />
                    </div>
                  </div>
                )}
                <p className="font-semibold">{getTranslatedValue(q, "question")}</p>
                <div className="mt-4 font-medium">{`${t("correctAnswer")}: ${correctAnswer}`}</div>
                <div className="mt-4 text-xs">
                  {`${t("yourAnswer")}: ${yourAnswer}`}
                  <span className="ml-4">{correct ? `✅` : `❌`}</span>
                </div>
              </div>
            );
          }
          return (
            <div key={q.question} className="text-md my-24 px-10 text-center">
              {photoUri && (
                <div className="mb-6 flex justify-center">
                  <div className="w-full max-w-2xs">
                    <FirebaseCachedImage key={photoUri} uri={photoUri} alt={getTranslatedValue(q, "question")} />
                  </div>
                </div>
              )}
              <p className=" font-semibold">{getTranslatedValue(q, "question")}</p>
              <div className=" mt-2">
                <span className="text-sm font-light">{correct ? t("correct") : t("incorrect")} </span>
                <span className="ml-4">{correct ? `✅` : `❌`}</span>
              </div>
            </div>
          );
        })}
        <Footer />
      </main>
    );
  }
  return (
    <>
      <main>
        <div className="px-6 py-4">
          <h1 className="mt-16 mb-10 text-center  text-3xl font-semibold">{about}</h1>
          <div className="mt-4 mb-8 flex flex-col items-center">
            <div className="mx-6">
              <div className="pb-6 text-center text-xl">{copy}</div>
              {challenge.introPhotoUri && (
                <div className="flex justify-center">
                  <div className="w-full max-w-xs">
                    <FirebaseCachedImage uri={challenge.introPhotoUri} alt={about} />
                  </div>
                </div>
              )}
              <div className="my-12 flex justify-center">
                <Link to="0">
                  <button className="btn-primary btn my-6">{t("start")}</button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};
